<template>
    <section class="about-enhanced">
        <div class="container">
            <div class="about-layout">
                <!-- Columna izquierda: About me y métricas -->
                <div class="about-content">
                    <h2 class="section-title">Acerca de mí</h2>

                    <p class="about-text" :class="{ clamped: !expanded }">
                        {{ description }}
                    </p>
                    <button class="read-more" @click="expanded = !expanded">
                        {{ expanded ? 'Leer menos' : 'Leer más' }}
                    </button>

                    <!-- Métricas -->
                    <div class="metrics-grid">
                        <div class="metric-box" v-for="metric in metrics" :key="metric.id">
                            <div class="metric-number">
                                {{ metric.number }}<span class="metric-symbol">{{ metric.suffix }}</span>
                            </div>
                            <div class="metric-label">{{ metric.label }}</div>
                        </div>
                    </div>
                </div>

                <!-- Columna derecha: Timeline con educación -->
                <div class="education-timeline">
                    <div class="timeline-item" v-for="(edu, index) in education" :key="index">
                        <div class="timeline-dot"></div>
                        <div class="education-icon">
                            <i class="fa fa-graduation-cap"></i>
                        </div>
                        <div class="education-info">
                            <h4 class="education-title">{{ edu.carrier }}</h4>
                            <p class="education-university">{{ edu.university }}</p>
                            <p class="education-date">{{ edu.date }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
export default {
    name: 'AboutMeEnhanced',
    props: {
        description: String,
        metrics: Array,
        education: Array
    },
    data() {
        return { expanded: false }
    }
}
</script>

<style scoped>
.about-enhanced {
    background: transparent;
    padding: 100px 0;
    color: #fff;
}

.about-layout {
    display: grid;
    grid-template-columns: 1fr 320px;
    gap: 80px;
    align-items: start;
}

/* About content */
.about-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.section-title {
    font-size: 48px;
    font-weight: 700;
    color: #fff;
    margin: 0 0 30px 0;
    align-self: flex-start;
}

/* Timeline de educación */
.education-timeline {
    position: relative;
    margin-top: 78px; /* Altura del título (48px) + margin-bottom (30px) = 78px */
}

.education-timeline::before {
    content: '';
    position: absolute;
    left: 25px;
    top: 40px;
    bottom: 40px;
    width: 2px;
    background: rgba(255, 255, 255, 0.1);
}

.timeline-item {
    position: relative;
    margin-bottom: 50px;
    padding-left: 80px;
}

.timeline-item:last-child {
    margin-bottom: 0;
}

.timeline-dot {
    position: absolute;
    left: 20px;
    top: 15px;
    width: 12px;
    height: 12px;
    background: #ff6b6b;
    border-radius: 50%;
    box-shadow: 0 0 0 4px #482d36;
}

.education-icon {
    width: 50px;
    height: 50px;
    background: #252834;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;
}

.education-icon i {
    font-size: 24px;
    color: #ff6b6b;
}

.education-info {
    margin-top: 8px;
}

.education-title {
    font-size: 15px;
    font-weight: 600;
    color: #fff;
    margin: 0 0 8px 0;
    line-height: 1.4;
    text-transform: uppercase;
}

.education-university {
    font-size: 14px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.7);
    margin: 0 0 6px 0;
    line-height: 1.4;
}

.education-date {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.5);
    margin: 0;
}

.about-text {
    font-size: 16px;
    line-height: 1.8;
    color: rgba(255, 255, 255, 0.7);
    margin: 0 0 60px 0;
}

.read-more {
    display: none;
}

/* Métricas */
.metrics-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 40px;
}

.metric-box {
    text-align: left;
}

.metric-number {
    font-size: 52px;
    font-weight: 700;
    color: #fff;
    line-height: 1;
    margin-bottom: 8px;
    display: flex;
    align-items: baseline;
    gap: 4px;
}

.metric-symbol {
    font-size: 36px;
    color: #ff6b6b;
    font-weight: 700;
}

.metric-label {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.6);
    font-weight: 400;
    line-height: 1.4;
}

/* Responsive */
@media (max-width: 1024px) {
    .about-layout {
        grid-template-columns: 1fr;
        gap: 60px;
    }

    .education-timeline {
        margin-top: 0;
    }

    .education-timeline::before {
        left: 25px;
        top: 40px;
        bottom: 40px;
    }

    .timeline-item {
        padding-left: 80px;
        margin-bottom: 50px;
    }

    .timeline-item:last-child {
        margin-bottom: 0;
    }

    .timeline-dot {
        display: block;
    }
}

@media (max-width: 768px) {
    .about-enhanced {
        padding: 60px 0;
    }

    .section-title {
        font-size: 36px;
        margin-bottom: 20px;
    }

    .about-text {
        font-size: 15px;
        margin-bottom: 10px;
    }

    /* Texto recortado con "Leer más" */
    .about-text.clamped {
        display: -webkit-box;
        -webkit-line-clamp: 5;
        line-clamp: 5;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    .read-more {
        display: inline-block;
        background: none;
        border: none;
        padding: 0;
        margin-bottom: 40px;
        color: #ff6b6b;
        font-size: 15px;
        font-weight: 600;
        cursor: pointer;
    }

    /* Educación compacta: ícono al lado del título */
    .education-timeline::before {
        left: 21px;
        top: 20px;
        bottom: 20px;
    }

    .timeline-item {
        display: grid;
        grid-template-columns: 44px 1fr;
        gap: 0 15px;
        align-items: start;
        padding-left: 45px;
        margin-bottom: 28px;
    }

    .timeline-dot {
        left: 16px;
        top: 16px;
    }

    .education-icon {
        width: 44px;
        height: 44px;
        margin-bottom: 0;
    }

    .education-icon i {
        font-size: 20px;
    }

    .education-info {
        margin-top: 0;
    }

    .metrics-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 30px;
    }

    .metric-number {
        font-size: 42px;
    }

    .metric-symbol {
        font-size: 30px;
    }
}

@media (max-width: 480px) {
    .metrics-grid {
        gap: 25px 20px;
    }

    .metric-number {
        font-size: 38px;
    }

    .metric-symbol {
        font-size: 26px;
    }
}
</style>
